import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircleQuestion, X, Send, Bot, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  'How do I add a resident?',
  'How do I assign chores?',
  'How do I set up a room and beds?',
  'How do I export HMIS data?',
];

const newSessionId = () =>
  (window.crypto?.randomUUID?.() || `help-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const HelpAssistant = ({ open, setOpen }) => {
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your ShelterConnectOS help assistant. Ask me how to do anything in the app — like \"How do I add a resident?\"" },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/help/chat`, { message, session_id: sessionId });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Sorry, I ran into a problem. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: msg }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="help-assistant-launcher"
          className="fixed bottom-24 right-6 z-[60] flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg px-4 py-3 transition-colors"
          title="Ask the Help Assistant"
        >
          <MessageCircleQuestion className="h-5 w-5" />
          <span className="hidden sm:inline text-sm font-medium">Help</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="help-assistant-panel"
          className="fixed bottom-24 right-6 z-[60] flex flex-col w-[92vw] max-w-md h-[70vh] max-h-[560px] rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <span className="font-semibold">Help Assistant</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="help-assistant-close" className="hover:bg-indigo-500 rounded p-1">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  data-testid={`help-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === 'user' ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="pt-2 space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="help-suggestion"
                    className="block w-full text-left text-sm text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-3 py-2 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 p-3 border-t border-gray-200 bg-white"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask how to use the app…"
              data-testid="help-assistant-input"
              className="flex-1"
              disabled={loading}
            />
            <Button type="submit" size="sm" disabled={loading || !input.trim()} data-testid="help-assistant-send" className="bg-indigo-600 hover:bg-indigo-700">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default HelpAssistant;
